import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useTenant } from '../hooks/useTenant';
import { useAuthRoles } from '../hooks/useAuthRoles';
import { useAccess } from '../hooks/useAccess';
import { usePermissions } from '../hooks/usePermissions';
import { roleLabels } from '../config/roles';
import { TenantSwitcher } from './TenantSwitcher';
import { PersonaSwitcher } from './PersonaSwitcher';
import {
  House,
  Building2,
  Settings2,
  SlidersHorizontal,
  ArrowLeftRight,
  BookOpen,
  ChevronsLeft,
  ChevronsRight,
  LogOut,
  X,
} from 'lucide-react';

// Sidebar width in px. AppShell gives the page the difference when the
// sidebar collapses, so keep these the single source for both.
export const SIDEBAR_WIDTH = { expanded: 260, collapsed: 76 };

// An item with `permission` is shown when the server reports that effective
// Hub permission (GET /profile/permissions, tenant-auditor restriction
// applied); the others are matched on token roles.
export const NAV_ITEMS = [
  { path: '/dashboard',     key: 'home',          labelKey: 'nav.home',         icon: House,           roles: ['*'] },
  { path: '/buildings',     key: 'buildings',     labelKey: 'nav.buildings',    icon: Building2,       permission: 'building:read' },
  { path: '/services',      key: 'services',      labelKey: 'nav.services',     icon: Settings2,       roles: ['*'] },
  { path: '/data-exchange', key: 'dataExchange',  labelKey: 'nav.dataExchange', icon: ArrowLeftRight,  permission: 'asset:read' },
  { path: '/wiki',          key: 'wikiAssistant', labelKey: 'nav.wikiAssistant',icon: BookOpen,        roles: ['*'] },
  { path: '/settings',      key: 'settings',      labelKey: 'nav.settings',     icon: SlidersHorizontal, roles: ['*'] },
];

export const roleCanSee = (item, roles, tenantRole, authRoles, hasPermission) => {
  if (item.roles?.includes('*')) return true;
  if (item.permission) return hasPermission(item.permission);
  if (roles.includes('system_admin')) return true;
  if (tenantRole === 'tenant_auditor') {
    return ['home', 'wikiAssistant', 'settings'].includes(item.key);
  }
  return item.roles?.some((role) => roles.includes(role) || authRoles.hasRealmRole(role)) || false;
};

const NavItem = ({ item, collapsed, onClick, t }) => {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      onClick={onClick}
      title={item.label}
      aria-label={t(item.labelKey)}
      className={({ isActive }) =>
        [
          'relative flex items-center rounded-xl text-[13px] font-semibold no-underline',
          'transition-colors duration-150 mb-0.5 group outline-none focus-ring',
          collapsed ? 'mx-auto h-9 w-12 justify-center px-0 py-0' : 'gap-3 px-3 py-2.5',
          isActive
            ? 'bg-white/[0.09] text-white sidebar-active-line'
            : 'text-white/70 hover:text-white/90 hover:bg-white/[0.07]',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          <Icon
            size={17}
            strokeWidth={isActive ? 2.1 : 1.7}
            className={
              isActive
                ? 'text-[#6BAF65] flex-shrink-0'
                : 'text-white/45 group-hover:text-white/70 flex-shrink-0 transition-colors'
            }
            aria-hidden="true"
          />
          <span
            aria-hidden="true"
            className={['overflow-hidden whitespace-nowrap transition-all duration-150', collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[200px]'].join(' ')}
          >{t(item.labelKey)}</span>
        </>
      )}
    </NavLink>
  );
};

/**
 * Sidebar — dark forest-green navigation panel.
 * Supports responsive collapse / mobile drawer close.
 */
export const Sidebar = ({ onClose, collapsed = false, onToggleCollapse }) => {
  const navigate  = useNavigate();
  const { signOut, user, primaryRole } = useAuth();
  const authRoles = useAuthRoles();
  const { activeTenant } = useTenant();
  const { personas, activePersona } = useAccess();
  const { has: hasPermission } = usePermissions();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const userName  = user?.full_name || user?.email?.split('@')?.[0] || 'User';
  // The persona the user is acting as (Business Case x persona model) wins over the first token role.
  const activePersonaLabel = personas.find((persona) => persona.role === activePersona)?.label;
  const roleLabel = activePersonaLabel || roleLabels[primaryRole] || 'No persona assigned';
  const initials  = userName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [primaryRole].filter(Boolean);
  const navItems = NAV_ITEMS.filter((item) => roleCanSee(item, roles, activeTenant?.role, authRoles, hasPermission));

  return (
    <aside
      className="h-full flex flex-col bg-lb-sidebar overflow-hidden transition-[width] duration-200 ease-out"
      style={{
        width: collapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded,
        boxShadow: '4px 0 40px rgba(0,0,0,0.18)',
      }}
      aria-label="Primary navigation"
    >
      {/* ── Brand header — full-width logo, wordmark baked into PNG ── */}
      <div className="flex-shrink-0 relative">
        <div className={['relative overflow-hidden transition-all duration-200', collapsed ? 'h-[84px]' : 'h-[150px]'].join(' ')}>
          <img
            src="/livebetter_sidebar_logo.png"
            alt="LiveBetter"
            className={['absolute inset-0 w-full h-full object-contain transition-opacity duration-200', collapsed ? 'opacity-0' : 'opacity-100'].join(' ')}
          />
          <img
            src="/livebetter_logo_mark.png"
            alt=""
            aria-hidden="true"
            className={['absolute inset-0 m-auto object-contain transition-opacity duration-200', collapsed ? 'opacity-100' : 'opacity-0'].join(' ')}
            style={{ width: '56px', height: '84px' }}
          />
        </div>
        {/* Mobile close — absolute so it doesn't affect logo layout */}
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden absolute top-3 right-3 border-0 appearance-none bg-transparent text-white/35 hover:text-white/65 transition-colors p-1 rounded-lg focus-ring"
          aria-label="Close navigation"
        >
          <X size={17} />
        </button>
        <div className="mx-3 mt-1 h-px bg-white/[0.07]" />
      </div>

      {/* ── Navigation items ── */}
      <nav
        className={[
          'flex-1 overflow-y-auto scrollbar-thin pb-2',
          collapsed ? 'px-0' : 'px-3',
        ].join(' ')}
        aria-label="Main sections"
      >
        {navItems.map((item) => (
          <NavItem key={item.key} item={item} collapsed={collapsed} onClick={onClose} t={t} />
        ))}
      </nav>

      {/* ── Utility links ── */}
      <div className="px-3 pt-2 flex-shrink-0">
        <div className="h-px bg-white/[0.07] mb-2" />
        <div className="mb-2 empty:hidden">
          <PersonaSwitcher collapsed={collapsed} />
        </div>
        <div className="mb-2">
          <TenantSwitcher placement="sidebar" collapsed={collapsed} />
        </div>
        <button
          type="button"
          onClick={onToggleCollapse}
          className={[
            'group flex items-center rounded-xl border-0 appearance-none bg-transparent',
            'text-[13px] font-semibold text-white/70 hover:text-white/90 hover:bg-white/[0.07]',
            'transition-colors duration-150 focus-ring outline-none mb-0.5',
            collapsed ? 'mx-auto h-9 w-12 justify-center px-0 py-0' : 'gap-3 w-full px-3 py-2.5',
          ].join(' ')}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-pressed={collapsed}
        >
          {collapsed ? (
            <ChevronsRight size={17} strokeWidth={1.8} className="text-white/45 group-hover:text-white/70 flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronsLeft size={17} strokeWidth={1.8} className="text-white/45 group-hover:text-white/70 flex-shrink-0" aria-hidden="true" />
          )}
          <span
            aria-hidden="true"
            className={['overflow-hidden whitespace-nowrap transition-all duration-150', collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[200px]'].join(' ')}
          >Collapse</span>
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className={[
            'group flex items-center rounded-xl border-0 appearance-none bg-transparent',
            'text-[13px] font-semibold text-white/70 hover:text-white/90 hover:bg-white/[0.07]',
            'transition-colors duration-150 focus-ring outline-none',
            collapsed ? 'mx-auto h-9 w-12 justify-center px-0 py-0' : 'gap-3 w-full px-3 py-2.5',
          ].join(' ')}
          aria-label={t('nav.logout')}
        >
          <LogOut size={17} strokeWidth={1.8} className="text-white/45 group-hover:text-white/70 flex-shrink-0" aria-hidden="true" />
          <span
            aria-hidden="true"
            className={['overflow-hidden whitespace-nowrap transition-all duration-150', collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[200px]'].join(' ')}
          >{t('nav.logout')}</span>
        </button>
      </div>

      {/* ── User profile card ── */}
      <div className="px-3 pb-5 pt-3 flex-shrink-0">
        <div
          className={[
            'bg-white/[0.07] rounded-2xl flex items-center',
            collapsed ? 'mx-auto h-[52px] w-[52px] justify-center p-0' : 'gap-3 p-3',
          ].join(' ')}
        >
          {user?.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={userName}
              className="w-9 h-9 rounded-xl object-cover flex-shrink-0"
            />
          ) : (
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #3D6B38, #6BAF65)' }}
              aria-hidden="true"
            >
              <span className="text-white text-[11px] font-bold tracking-wide">
                {initials}
              </span>
            </div>
          )}
          <div aria-hidden={collapsed} className={['overflow-hidden min-w-0 transition-all duration-150', collapsed ? 'opacity-0 w-0' : 'flex-1 opacity-100'].join(' ')}>
            <p className="text-white/90 text-[13px] font-semibold truncate leading-tight">
              {userName}
            </p>
            <p className="text-[11px] truncate mt-0.5 leading-tight" style={{ color: '#6BAF65' }}>
              {roleLabel}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};
